// Copyright (c) Ping. All rights reserved.
//
// Shared UI-preference store. Anything that needs to survive a reload
// AND is local to a single device (panel widths, last-selected tabs,
// disclosure-toggle state) belongs here so we don't sprinkle
// `localStorage.getItem`/`setItem` across the codebase.
//
// Persistence is opt-in per platform:
//   • web  — `localStorage` via zustand's `createJSONStorage`.
//   • macOS — no-op (`localStorage` is undefined). The store still
//     works in-memory; values just reset on relaunch, which is what
//     the desktop shell wants today (Figma desktop is fixed-width).

import { create } from 'zustand';
import {
	createJSONStorage,
	persist,
	type PersistStorage,
	type StateStorage,
} from 'zustand/middleware';

// Width clamps for the chat-home conversations panel. `MIN_WIDTH` is
// the drag floor; `DEFAULT_WIDTH` matches the Figma chat-home list
// panel (420 px) so first-run opens at the designed width. `MAX_WIDTH`
// keeps the main content pane wide enough to read messages comfortably.
export const SIDE_PANEL_MIN_WIDTH = 339;
export const SIDE_PANEL_DEFAULT_WIDTH = SIDE_PANEL_MIN_WIDTH;
export const SIDE_PANEL_MAX_WIDTH = 560;

// Width clamps for the chat right-utility panel (Threads / Notes /
// Tasks). Default matches the Figma desktop right panel (~409 px).
export const RIGHT_PANEL_MIN_WIDTH = 320;
export const RIGHT_PANEL_DEFAULT_WIDTH = RIGHT_PANEL_MIN_WIDTH;
export const RIGHT_PANEL_MAX_WIDTH = 640;

// Chat message layout density. "bubble" = classic rounded bubbles with
// tails (Figma 2804:25343); "stacked" = Slack/Discord-style compact
// rows with a left avatar gutter and no bubble background (Figma
// 2804:25812). User-selectable from Settings → Appearance.
export type MessageLayoutMode = 'bubble' | 'stacked';

// Which slide a panel docks to. Chat is always the flexible centre
// column; the conversations list and the utility (Threads/Notes/Tasks)
// panel each pick a side. Configurable from Settings → Sidebar.
export type PanelSide = 'left' | 'right';

// Active right-utility panel surface. Threads are tracked separately
// (per-message, in the chat shell view-model) and take precedence when
// open; this controls the Notes/Tasks toggles from the TopBar.
export type ChatUtilityPanel = 'none' | 'notes' | 'tasks';

export interface UiPreferencesState {
	/** Width (px) of the conversations side-panel. Clamped on set. */
	readonly sidePanelWidth: number;
	setSidePanelWidth: (next: number) => void;
	/** Width (px) of the chat right-utility panel. Clamped on set. */
	readonly rightPanelWidth: number;
	setRightPanelWidth: (next: number) => void;
	/** Chat message layout density — bubble vs stacked/compact. */
	readonly messageLayout: MessageLayoutMode;
	setMessageLayout: (next: MessageLayoutMode) => void;
	/** Side the conversations list docks to (default left). */
	readonly conversationsSide: PanelSide;
	setConversationsSide: (next: PanelSide) => void;
	/** Side the Threads/Notes/Tasks utility panel docks to (default right).
	 *  Retained for back-compat / migration; per-panel sides below now
	 *  drive placement individually. */
	readonly utilityPanelSide: PanelSide;
	setUtilityPanelSide: (next: PanelSide) => void;
	/** Per-utility-panel dock sides (each independently left/right). */
	readonly threadsSide: PanelSide;
	setThreadsSide: (next: PanelSide) => void;
	readonly notesSide: PanelSide;
	setNotesSide: (next: PanelSide) => void;
	readonly tasksSide: PanelSide;
	setTasksSide: (next: PanelSide) => void;
	readonly profileSide: PanelSide;
	setProfileSide: (next: PanelSide) => void;
	/** Currently-open utility panel (Notes/Tasks); 'none' when closed. */
	readonly chatUtilityPanel: ChatUtilityPanel;
	setChatUtilityPanel: (next: ChatUtilityPanel) => void;
	/** Toggle a utility panel — opening it, or closing if already open. */
	toggleChatUtilityPanel: (panel: Exclude<ChatUtilityPanel, 'none'>) => void;
	/** User-pinned sidebar item keys (beyond the fixed chat/activity/contacts). */
	readonly sidebarPinnedItems: readonly string[];
	setSidebarPinnedItems: (items: readonly string[]) => void;
	/** Custom display order for all customizable sidebar items. */
	readonly sidebarItemOrder: readonly string[];
	setSidebarItemOrder: (order: readonly string[]) => void;
}

function clampPanelWidth(value: number): number {
	if (!Number.isFinite(value)) return SIDE_PANEL_DEFAULT_WIDTH;
	return Math.min(Math.max(value, SIDE_PANEL_MIN_WIDTH), SIDE_PANEL_MAX_WIDTH);
}

function clampRightPanelWidth(value: number): number {
	if (!Number.isFinite(value)) return RIGHT_PANEL_DEFAULT_WIDTH;
	return Math.min(
		Math.max(value, RIGHT_PANEL_MIN_WIDTH),
		RIGHT_PANEL_MAX_WIDTH,
	);
}

// `localStorage` is the host-provided backend on web. On macOS the
// global is undefined; we fall back to a noop storage so the persist
// middleware doesn't throw at module load.
function resolveStorage(): PersistStorage<UiPreferencesState> | undefined {
	if (typeof globalThis === 'undefined') return undefined;
	const ls = (globalThis as { localStorage?: StateStorage }).localStorage;
	if (ls === undefined) return undefined;
	return createJSONStorage<UiPreferencesState>(() => ls);
}

export const useUiPreferencesStore = create<UiPreferencesState>()(
	persist(
		(set) => ({
			sidePanelWidth: SIDE_PANEL_DEFAULT_WIDTH,
			setSidePanelWidth: (next) => {
				set({ sidePanelWidth: clampPanelWidth(next) });
			},
			rightPanelWidth: RIGHT_PANEL_DEFAULT_WIDTH,
			setRightPanelWidth: (next) => {
				set({ rightPanelWidth: clampRightPanelWidth(next) });
			},
			messageLayout: 'bubble',
			setMessageLayout: (next) => {
				set({ messageLayout: next });
			},
			conversationsSide: 'left',
			setConversationsSide: (next) => {
				set({ conversationsSide: next });
			},
			utilityPanelSide: 'right',
			setUtilityPanelSide: (next) => {
				set({ utilityPanelSide: next });
			},
			threadsSide: 'right',
			setThreadsSide: (next) => {
				set({ threadsSide: next });
			},
			notesSide: 'right',
			setNotesSide: (next) => {
				set({ notesSide: next });
			},
			tasksSide: 'right',
			setTasksSide: (next) => {
				set({ tasksSide: next });
			},
			profileSide: 'right',
			setProfileSide: (next) => {
				set({ profileSide: next });
			},
			chatUtilityPanel: 'none',
			setChatUtilityPanel: (next) => {
				set({ chatUtilityPanel: next });
			},
			toggleChatUtilityPanel: (panel) => {
				set((state) => ({
					chatUtilityPanel:
						state.chatUtilityPanel === panel ? 'none' : panel,
				}));
			},
			sidebarPinnedItems: [],
			setSidebarPinnedItems: (items) => {
				set({ sidebarPinnedItems: items });
			},
			sidebarItemOrder: [],
			setSidebarItemOrder: (order) => {
				set({ sidebarItemOrder: order });
			},
		}),
		{
			name: 'com.ping.ui.preferences',
			version: 6,
			// Each version only ADDS fields with safe defaults — older blobs
			// migrate forward untouched; zustand merges missing keys from
			// initial state. Without this `migrate`, a version bump silently
			// DROPS all persisted prefs.
			//
			// v6: drop any persisted sidebar pin/order so the new default
			// (nothing pinned beyond the fixed Chat/History/Contacts) takes
			// effect. v5 shipped a stray `['calls']` default; stripping the
			// keys lets zustand re-seed them from initial state.
			migrate: (persisted, version) => {
				if (version < 6 && persisted !== null && typeof persisted === 'object') {
					const { sidebarPinnedItems: _p, sidebarItemOrder: _o, ...rest } =
						persisted as Record<string, unknown>;
					return rest as unknown as UiPreferencesState;
				}
				return persisted as UiPreferencesState;
			},
			...(resolveStorage() !== undefined ? { storage: resolveStorage() } : {}),
			// Only persist user-facing values, not action functions.
			partialize: (state) =>
				({
					sidePanelWidth: state.sidePanelWidth,
					rightPanelWidth: state.rightPanelWidth,
					messageLayout: state.messageLayout,
					conversationsSide: state.conversationsSide,
					utilityPanelSide: state.utilityPanelSide,
					threadsSide: state.threadsSide,
					notesSide: state.notesSide,
					tasksSide: state.tasksSide,
					profileSide: state.profileSide,
					chatUtilityPanel: state.chatUtilityPanel,
					sidebarPinnedItems: state.sidebarPinnedItems,
					sidebarItemOrder: state.sidebarItemOrder,
				}) as UiPreferencesState,
		},
	),
);
